import { z } from "zod";

/**
 * Workspace and list-level policy for sending to risky (catch-all / unverifiable) email addresses
 * (PREC-4261, slice 1/14). Stored in `configuration` (`RISKY_EMAIL_POLICY`) at workspace scope and
 * in `list.riskyEmailPolicy` (VARCHAR(16), nullable) for per-list overrides.
 */
export enum RiskyEmailPolicy {
	NEVER = "NEVER",
	AUTO = "AUTO",
	ALWAYS = "ALWAYS",
}

export const RISKY_EMAIL_POLICIES = [
	RiskyEmailPolicy.NEVER,
	RiskyEmailPolicy.AUTO,
	RiskyEmailPolicy.ALWAYS,
] as const;

export const DEFAULT_RISKY_EMAIL_POLICY = RiskyEmailPolicy.AUTO;

export const riskyEmailPolicySchema = z.nativeEnum(RiskyEmailPolicy);

export function isRiskyEmailPolicy(value: unknown): value is RiskyEmailPolicy {
	return (
		typeof value === "string" &&
		(RISKY_EMAIL_POLICIES as readonly string[]).includes(value)
	);
}

/**
 * Default workspace email deliverability settings when no `configuration` rows have been saved yet
 * (PREC-4261). Email verification charges credits, so it is off until a workspace or a list turns
 * it on (PREC-4265).
 */
export const DEFAULT_EMAIL_DELIVERABILITY_SETTINGS = {
	emailVerificationEnabled: false,
	riskyEmailPolicy: RiskyEmailPolicy.AUTO,
	riskyEmailPauseAbove4Percent: true,
	bounceRepairEnabled: true,
	bounceRepairReviewOnCompanyChange: true,
} as const;

/**
 * Feature-availability constants for the four email-deliverability capabilities (PREC-4261, slice
 * 1/14). Every constant stays `false` in slice 1/14 so no control claims a capability whose
 * backend slice has not landed yet (root CLAUDE.md, "A control names what it will do"); later slices
 * flip their own constant to `true` as they land:
 * - Slice 5/14: email verification (`EMAIL_VERIFICATION_SETTINGS_ENABLED`)
 * - Slice 6/14: automatic bounce/unsubscribe suppression (`EMAIL_SUPPRESSION_SETTINGS_ENABLED`)
 * - Slice 8/14: bounce repair + company-change routing (`BOUNCE_REPAIR_SETTINGS_ENABLED`)
 * - Slice 10/14: risky-email policy + 4% bounce auto-pause (`RISKY_EMAIL_POLICY_SETTINGS_ENABLED`)
 */
export const EMAIL_VERIFICATION_SETTINGS_ENABLED = false;
export const RISKY_EMAIL_POLICY_SETTINGS_ENABLED = false;
export const BOUNCE_REPAIR_SETTINGS_ENABLED = false;
export const EMAIL_SUPPRESSION_SETTINGS_ENABLED = false;

export interface EmailDeliverabilityFeatureAvailability {
	verification: boolean;
	riskyEmailPolicy: boolean;
	bounceRepair: boolean;
	suppression: boolean;
}

export const EMAIL_DELIVERABILITY_FEATURE_AVAILABILITY: EmailDeliverabilityFeatureAvailability = {
	verification: EMAIL_VERIFICATION_SETTINGS_ENABLED,
	riskyEmailPolicy: RISKY_EMAIL_POLICY_SETTINGS_ENABLED,
	bounceRepair: BOUNCE_REPAIR_SETTINGS_ENABLED,
	suppression: EMAIL_SUPPRESSION_SETTINGS_ENABLED,
};

export function hasAnyEmailDeliverabilityFeature(
	availability: EmailDeliverabilityFeatureAvailability = EMAIL_DELIVERABILITY_FEATURE_AVAILABILITY
): boolean {
	return (
		availability.verification ||
		availability.riskyEmailPolicy ||
		availability.bounceRepair ||
		availability.suppression
	);
}
